import { ParlorRequestExperiment } from "@/components/ParlorRequestExperiment";
import { isLoggedInState } from "@/atoms/accountState";
import { getCookie, VStack, DevWarningBanner } from "goi_common";
import { useRouter } from "next/router";
import React, { useEffect, useMemo } from "react";
import { useRecoilValue } from "recoil";

import GuidebookSummaryHeader from "./GuidebookSummaryHeader";

import { Modal, ModalOverlay, useDisclosure } from "@chakra-ui/react";
import StepGuideFooter from "../common/StepGuideFooter";

import { Header } from "./Header";
import Menu from "./Menu";
import { FacilityRequestRollbackProvider } from "@/components/FacilityRequestRollbackExperiment";
import { FacilityDetailCostHubExperimentProvider } from "@/components/FacilityDetailCostHubExperiment";
import { ReviewRenewalExperimentProvider } from "@/components/ReviewRenewalExperiment";

interface LayoutProps {
  children: React.ReactNode;
}
import * as ChannelService from "@channel.io/channel-web-sdk-loader";
import { useOverlay } from "@toss/use-overlay";
import { useCheckGoogleSA } from "@/hooks/useCheckGoogleSA";
import { useTrackCostComparisonEntry } from "@/hooks/useTrackCostComparisonEntry";
import { useTrackBrandSearchAdEntry } from "@/hooks/useTrackBrandSearchAdEntry";
import { useTrackFacilityVisit } from "@/hooks/useTrackFacilityVisit";
import { useChuseokOff, isChuseokOffPath } from "@/hooks/useChuseokOff";
// import { useOffDirectCall } from "@/hooks/useOffDirectCall";

ChannelService.loadScript();

export default function Layout({ children }: LayoutProps) {
  const router = useRouter();
  const overlay = useOverlay();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const isGuideBookSummary = useMemo(() => {
    return router.asPath.startsWith("/guidebook/summary/");
  }, [router]);

  const { setIsCheckSearchAdInCookie, setIsCheckPageType } = useCheckGoogleSA();
  const { trackEntry: trackCostComparisonEntry } = useTrackCostComparisonEntry();
  const { trackEntry: trackBrandSearchAdEntry } = useTrackBrandSearchAdEntry();
  const { trackVisit: trackFacilityVisit } = useTrackFacilityVisit();

  useEffect(() => {
    if (!router.isReady) return;
    if (router.asPath.includes("login")) return;
    /** 검색광고 쿠키 세팅 */
    setIsCheckSearchAdInCookie();
    /** 페이지 타입 쿠키 세팅 */
    setIsCheckPageType();
    /** 광고/dogsound 진입자 마커 세팅 (숨은비용 분기용) */
    trackCostComparisonEntry();
    /** 브랜드 검색광고 진입자 마커 세팅 (여유 결과화면 분기용, 24h) */
    trackBrandSearchAdEntry();
    /** 시설 페이지 방문 마커 세팅 (실험 282 모수 제외용, sessionStorage) */
    trackFacilityVisit();
  }, [router.isReady]);

  // const 오인지_최소화_기능 = useOffDirectCall();
  // const workshopHidden =
  //   오인지_최소화_기능 &&
  //   (router.asPath.includes("facilities") || router.asPath.includes("facility"));

  // const isOffDirectCall = useOffDirectCall();

  // const isHiddenByOffDirectCall = useMemo(() => {
  //   return (
  //     isOffDirectCall &&
  //     (router.asPath.includes("/crematorium") || router.asPath.includes("/facilities/"))
  //   );
  // }, [router, isOffDirectCall]);

  /**
   * 2026 추석. 콜이 발생하면 안 되는 시설 페이지에서는 헤더를 통째로 내린다.
   * 헤더에 곧 붙는 시설탐색까지 같이 덮으려고 메뉴별이 아니라 경로 기준으로 잡는다.
   */
  const isChuseokOff = useChuseokOff();
  const isChuseokHeaderHidden = isChuseokOff && isChuseokOffPath(router.asPath);

  const isHeaderHidden = useMemo(() => {
    return (
      isChuseokHeaderHidden ||
      router.route === "/login" ||
      router.route === "/login-redirect" ||
      isGuideBookSummary ||
      // router.pathname === "/parlor/[id]" ||
      router.pathname === "/match/facility-requests" ||
      router.pathname === "/funeral-prepare/1" ||
      router.asPath.startsWith("/partners/") ||
      router.asPath.includes("/refund/") ||
      router.asPath.startsWith("/promotion/jangji") ||
      router.asPath.startsWith("/content/feeds/caring") ||
      router.asPath.startsWith("/content/feeds/mubinso") ||
      router.asPath.startsWith("/owned-request") ||
      router.pathname.startsWith("/funeral-facilities") ||
      router.pathname.startsWith("/my/v3") ||
      router.pathname === "/go/[slug]"
      // || isHiddenByOffDirectCall
      // || workshopHidden
    );
  }, [
    router,
    isChuseokHeaderHidden,
    // workshopHidden
  ]);

  const isChecklistFabVisible = useMemo(() => {
    const checklistPages = [
      // "/product",
      "/service/[type]",
      "/promotion/service/[type]/premium-2025",
      "/parlors/region/[code]",
      "/parlors/region/[code]/[sigungu]",
      "/parlor",
      "/parlor/[id]/prices/[type]",
      "/facilities/sea-burial/[id]",
      "/facilities/region/[type]/[code]", // 수목장, 납골당
      "/crematorium/[id]",
      "/crematorium-recommendation/[id]",
    ];

    return checklistPages.includes(router.pathname);
  }, [router]);

  const isLoggedIn = useRecoilValue(isLoggedInState);

  const quotationRequestCRMDrawerOpen = () => {
    const isLoginPath = router.asPath.startsWith("/login");

    if (isLoggedIn || isLoginPath) return;
  };

  useEffect(() => {
    if (!router.asPath.includes("login")) {
      ChannelService.showChannelButton();
    }
    quotationRequestCRMDrawerOpen();
  }, [router.asPath]);

  useEffect(() => {
    if (getCookie("isChannelTalkOpen") === "TRUE") {
      ChannelService.boot({
        pluginKey: process.env.NEXT_PUBLIC_CHANNELTALK_PLUGIN_KEY as string,
      });
    }
    ChannelService.onShowMessenger(() => {
      overlay.open(({ isOpen, close: onClose }) => {
        return (
          <Modal
            isOpen={isOpen}
            onClose={() => {
              onClose();
            }}
            closeOnOverlayClick={false}
            scrollBehavior="inside"
            isCentered={true}
            autoFocus={false}
            closeOnEsc={false}
            returnFocusOnClose={false}
          >
            <ModalOverlay width="100%" height="100%" />
          </Modal>
        );
      });
    });
    ChannelService.onHideMessenger(() => {
      overlay.close();
    });

    return () => {
      ChannelService.shutdown();
    };
  }, []);

  const getChecklistFabBottomOffset = useMemo(() => {
    if (router.route.startsWith("/guidebook") && !router.route.includes("/guidebook/tip/")) {
      return "72px";
    }

    const pageOffsets: Record<string, string> = {
      "/service/[type]": "108px",
      "/promotion/service/[type]/premium-2025": "108px",
      "/facilities/sea-burial/[id]": "90px",
      "/crematorium/[id]": "94px", // 하단 sticky CTA 바(~78px)와 겹침 방지
      "/crematorium-recommendation/[id]": "94px", // 하단 sticky CTA 바(~78px)와 겹침 방지
    };

    const base = pageOffsets[router.route] || "38px";

    /*
     * 화면에 떴다 사라지는 하단 고정 CTA 가 있으면 그 높이만큼 더 띄운다.
     *
     * 이 함수의 값은 라우트 단위라 "스크롤하면 나타나는 버튼" 을 표현할 수 없고, 실험 화면에만
     * 있는 버튼을 여기에 상수로 박으면 대조군 화면까지 같이 바뀐다. 그래서 그 버튼이 자기
     * 높이를 CSS 변수로 알려주고 여기서 더하기만 한다.
     *
     * 변수를 세팅하는 화면이 없으면 `0px` 로 떨어져 기존 동작 그대로다
     * (`RegionCostFrameFixedCta` 의 FAB_OFFSET_VAR 참고).
     */
    return `calc(${base} + var(--goi-fixed-cta-offset, 0px))`;
  }, [router.route]);

  return (
    // 실험 #338(장지 견적 롤백)의 단일 소스. 헤더와 견적 퍼널이 같은 팔을 봐야 해서
    // 둘을 모두 덮는 최상단에 둔다. 모수 밖 라우트에서는 훅 자체를 부르지 않는다.
    <ParlorRequestExperiment>
      <FacilityDetailCostHubExperimentProvider>
        <ReviewRenewalExperimentProvider>
          <FacilityRequestRollbackProvider>
            {!(process.env.NEXT_PUBLIC_IS_PRODUCTION === "true") && <DevWarningBanner />}
            {!isHeaderHidden && <Header onOpen={onOpen} />}
            {isGuideBookSummary && <GuidebookSummaryHeader />}
            <VStack>{children}</VStack>

            {isChecklistFabVisible && (
              <StepGuideFooter
                text={
                  <>
                    장례가 처음이신가요? 합리적인 장례,
                    <br />
                    차근차근 알려드릴게요.
                  </>
                }
                size="30px"
                src="/step-guide/facility-initiation/"
                event={{
                  newGtm: {
                    path: "step_guide",
                    trigger_and_target: "step_guide_fixed_button",
                  },
                }}
                maxWidth="100%"
                bottom={getChecklistFabBottomOffset}
              />
            )}

            <Menu isOpen={isOpen} onClose={onClose} />
          </FacilityRequestRollbackProvider>
        </ReviewRenewalExperimentProvider>
      </FacilityDetailCostHubExperimentProvider>
    </ParlorRequestExperiment>
  );
}
